import { Form, Modal, notification } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Patientportal_PortalUserMsg, useUpdatePortalUserMutation } from '@/generated/graphql';
import { Maybe } from 'graphql/jsutils/Maybe';
import { buildPortalUserUpdateInput } from '../../helpers/portal-user';
import { PhoneNumber, validatePhoneNumber } from '../../helpers/utils';
import { AveliosButton } from '../common-layout/avelios-button';
import { PhoneInput } from '../common-layout/phone-input';

interface ChangePhoneNumberModalProps {
  currentUser: Maybe<Patientportal_PortalUserMsg>;
  currentUserLoading?: boolean;
  open: boolean;
  onClose: () => void;
}

export const ChangePhoneNumberModal: FC<ChangePhoneNumberModalProps> = ({
  currentUser,
  currentUserLoading = false,
  open,
  onClose,
}) => {
  const { formatMessage } = useIntl();

  const [phone, setPhone] = useState<PhoneNumber | undefined>(undefined);

  useEffect(() => {
    if (currentUser?.phoneNumber && currentUser?.phoneNumberCountryCode) {
      setPhone({
        number: currentUser.phoneNumber,
        countryCode: currentUser.phoneNumberCountryCode,
      });
    } else {
      setPhone(undefined);
    }
  }, [currentUser?.phoneNumber, currentUser?.phoneNumberCountryCode]);

  const [updatePortalUser, { loading: updateLoading }] = useUpdatePortalUserMutation({
    onCompleted: () => {
      notification.success({
        message: formatMessage({ id: 'change-phone-number.success' }),
      });
      onClose();
    },
    onError: () => {
      notification.error({
        message: formatMessage({ id: 'change-phone-number.error' }),
      });
    },
  });

  const loading = currentUserLoading || updateLoading;
  const phoneNumberIsValid = !!phone?.number && validatePhoneNumber(String(phone.number));
  const canSave = !!phone?.number && !!phone?.countryCode && phoneNumberIsValid;

  const handleSave = () => {
    updatePortalUser({
      variables: {
        input: {
          ...buildPortalUserUpdateInput(currentUser),
          phoneNumber: phone?.number,
          phoneNumberCountryCode: phone?.countryCode,
        },
      },
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={
        <AveliosButton loading={loading} type="primary" onClick={handleSave} disabled={!canSave}>
          {formatMessage({ id: 'action.save' })}
        </AveliosButton>
      }
      closable={true}
      title={<h2>{formatMessage({ id: 'change-phone-number' })}</h2>}
    >
      <Form layout="vertical">
        <Form.Item
          name="phone"
          label={formatMessage({ id: 'user.phone' })}
          required
          rules={[
            {
              required: true,
              message: formatMessage({ id: 'signup.required-field' }),
            },
            {
              validator: (_, value) =>
                validatePhoneNumber(value)
                  ? Promise.resolve()
                  : Promise.reject(new Error(formatMessage({ id: 'signup.invalid-phone' }))),
            },
          ]}
        >
          <PhoneInput currentValue={phone} onUpdate={setPhone} disabled={loading} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
